'use client';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { ArrowUpIcon } from 'lucide-react';
import { cn } from '@util/index';
import { CloseIcon } from '@c/icons';
import SafeImage from '@ui/SafeImage';
import { SupportBotButton } from 'features/support-bot/components/SupportBotButton';

export interface ChatInputProps {
  placeholder?: string;
  message: string;
  setMessage: (message: string) => void;
  onSend: () => void;
  onAttachment?: () => void;
  disabled?: boolean;
  className?: string;
  fileList?: File[];
  onFileListChange?: (files: File[]) => void;
  convertingImages?: boolean;
  uploadingImages?: boolean;
  onImageClick?: (index: number) => void;
  chatId: string;
  onDropFiles?: (files: File[]) => void;
  isSupport?: boolean;
}

const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      placeholder = 'Write your message here...',
      message,
      setMessage,
      onSend,
      onAttachment,
      disabled = false,
      className,
      fileList = [],
      onFileListChange,
      convertingImages = false,
      uploadingImages = false,
      onImageClick,
      chatId,
      onDropFiles,
    },
    ref
  ) => {
    const dragCounter = useRef<number>(0);
    const [isSingleLine, setIsSingleLine] = useState<boolean>(true);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const TEXTAREA_HEIGHT = '4.5rem';

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);

        // Auto-resize textarea without reset to prevent flashing
        const newHeight = Math.max(e.target.scrollHeight, 72);
        e.target.style.height = `${newHeight}px`;

        // Check if content is single line by comparing scroll height to base height
        // Base height is approximately 72px (4.5rem), scrollHeight > 72 indicates wrapping
        const isMultiLine = e.target.scrollHeight > 72;
        setIsSingleLine(!isMultiLine);
      },
      [setMessage]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter') return;
        if (e.shiftKey) return;

        e.preventDefault();
        if (
          !disabled &&
          !uploadingImages &&
          (message.trim() || fileList.length > 0)
        ) {
          onSend();
        }
      },
      [onSend, disabled, uploadingImages, message, fileList]
    );

    const handleSendClick = useCallback(() => {
      if (
        !disabled &&
        !uploadingImages &&
        (message.trim() || fileList.length > 0)
      ) {
        onSend();
      }
    }, [onSend, disabled, uploadingImages, message, fileList]);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragOver(false);
      }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const imageFiles = files.filter((file) =>
          file.type.startsWith('image/')
        );

        if (imageFiles.length > 0 && onDropFiles) {
          onDropFiles(imageFiles);
        }
      },
      [onDropFiles]
    );

    const isActive = message.trim().length > 0 || fileList.length > 0;
    const isDisabledOrUploading = disabled || uploadingImages;

    return (
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-10 mx-auto w-full bg-white px-4 py-4 sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:w-[calc(100%-6rem)] sm:px-0 sm:py-8 md:w-[calc(100%-8rem)] lg:w-[calc(100%-10rem)]',
          className
        )}
      >
        {fileList.length > 0 && (
          <div className="relative mb-4">
            <div className="grid grid-cols-7 gap-4 sm:flex">
              {fileList.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="relative h-[6rem] w-[6rem] rounded-[2rem] border border-zinc-400"
                >
                  <SafeImage
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    height={64}
                    width={64}
                    className="h-[6rem] min-h-[6rem] w-[6rem] min-w-[6rem] cursor-pointer rounded-[1rem] object-cover transition-opacity hover:opacity-80"
                    onClick={() => onImageClick?.(index)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-2 z-[100] flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full bg-zinc-800 p-2"
                    onClick={() => {
                      if (onFileListChange) {
                        const newFileList = fileList.filter(
                          (_: File, i: number) => i !== index
                        );
                        onFileListChange(newFileList);
                      }
                    }}
                    aria-label="Remove image"
                  >
                    <CloseIcon className="h-full w-full text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {convertingImages && (
          <div className="my-4 flex h-[6rem] w-[6rem] animate-pulse items-center gap-[1.6rem] rounded-brand border-[1.5px] border-black bg-slate-500 px-6 transition-[border-radius] duration-500" />
        )}

        {uploadingImages && (
          <div className="relative mb-4">
            <div className="grid grid-cols-7 gap-4 sm:flex">
              <div className="relative h-[6rem] w-[6rem] rounded-[2rem]">
                <div className="h-[6rem] w-[6rem] animate-pulse rounded-[1rem] bg-gray-200" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-secondary border-t-transparent" />
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={cn(
            'flex items-center gap-4 border px-4 transition-all duration-200',
            isDragOver
              ? 'border-2 border-blue-400 bg-blue-50'
              : 'border-zinc-300 bg-[#F6F6F6]',
            (ref as React.RefObject<HTMLTextAreaElement>)?.current
              ?.scrollHeight &&
              (ref as React.RefObject<HTMLTextAreaElement>)?.current &&
              (ref as React.RefObject<HTMLTextAreaElement>).current &&
              ((ref as React.RefObject<HTMLTextAreaElement>).current
                ?.scrollHeight || 0) > 72
              ? 'rounded-[2rem]'
              : 'rounded-full'
          )}
          style={{
            minHeight: '6rem',
          }}
        >
          {onAttachment && (
            <button
              type="button"
              onClick={onAttachment}
              className={cn(
                'flex-shrink-0 p-2',
                disabled || uploadingImages
                  ? 'cursor-not-allowed opacity-50'
                  : ''
              )}
              disabled={disabled || uploadingImages}
            >
              <ImagesIcon className="h-12 w-12 text-zinc-600" />
            </button>
          )}

          <textarea
            ref={ref}
            className={cn(
              'flex-1 resize-none overflow-hidden border-none bg-transparent px-4 py-5 text-[1.4rem] leading-tight outline-none ring-0 transition-all duration-150 ease-out placeholder:text-zinc-500 focus:border-none focus:outline-none focus:ring-0 sm:text-[1.6rem]',
              uploadingImages ? 'opacity-60' : ''
            )}
            style={{
              height: TEXTAREA_HEIGHT,
              border: 'none !important',
            }}
            value={message}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            placeholder={uploadingImages ? 'Uploading photo...' : placeholder}
            disabled={isDisabledOrUploading}
          />

          <div className="flex items-center gap-4">
            <SupportBotButton
              chat_id={chatId}
              onNextMessage={(message) => {
                setMessage(message);
              }}
              currentMessage={message}
            />

            <button
              type="button"
              onClick={handleSendClick}
              className={cn(
                'flex aspect-square h-[4.5rem] w-[4.5rem] flex-shrink-0 items-center justify-center rounded-full border-2 p-4 text-white transition-colors',
                isActive && !isDisabledOrUploading
                  ? 'bg-brand-secondary text-white hover:bg-brand-secondary-darker'
                  : 'cursor-not-allowed bg-[#D9D9D9] text-[#7F7F7F]'
              )}
              disabled={isDisabledOrUploading || !isActive}
            >
              {uploadingImages ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <ArrowUpIcon className="h-full w-full" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;

const ImagesIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Attach image</title>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21,15 16,10 5,21" />
    </svg>
  );
};
